/**
 * Player Display Component
 * 
 * Shows the names of both players in the match with visual indicators
 * for who is currently connected and ready.
 */

import React, { useEffect, useState } from 'react';
import { getFirestore, collection, query, where, limit, onSnapshot, QuerySnapshot } from 'firebase/firestore';
import styles from './PlayerDisplay.module.css';

interface PlayerDisplayProps {
  matchId: string;
  currentPlayerId: 'player1' | 'player2';
  player1Ready?: boolean;
  player2Ready?: boolean;
  player1Connected?: boolean;
  player2Connected?: boolean;
  /** Player 1 display name (passed from socket, avoids Firestore query) */
  player1DisplayName?: string | null;
  /** Player 2 display name (passed from socket, avoids Firestore query) */
  player2DisplayName?: string | null;
}

interface BattleData {
  creatorDisplayName?: string;
  player2DisplayName?: string;
  players?: {
    player1: string;
    player2: string | null;
  };
}

export const PlayerDisplay: React.FC<PlayerDisplayProps> = ({
  matchId,
  currentPlayerId,
  player1Ready = false,
  player2Ready = false,
  player1Connected = false,
  player2Connected = false,
  player1DisplayName: player1DisplayNameProp,
  player2DisplayName: player2DisplayNameProp,
}) => {
  const [player1Name, setPlayer1Name] = useState<string>('Waiting...');
  const [player2Name, setPlayer2Name] = useState<string>('Waiting for opponent...');
  const [loading, setLoading] = useState(true);

  // If names are provided via props (from socket), use them directly
  useEffect(() => {
    if (player1DisplayNameProp) {
      setPlayer1Name(player1DisplayNameProp);
    }
    if (player2DisplayNameProp) {
      setPlayer2Name(player2DisplayNameProp);
    }
    // If we have at least player1 name from props, consider loading done
    if (player1DisplayNameProp) {
      setLoading(false);
    }
  }, [player1DisplayNameProp, player2DisplayNameProp]);

  useEffect(() => {
    // Skip Firestore query if names are provided via props
    if (player1DisplayNameProp) {
      return;
    }

    // Find the battle document by matchId (fallback for backward compatibility)
    const firestore = getFirestore();

    // First, query for the battle with this matchId
    const battlesCollection = collection(firestore, 'battles');
    const q = query(battlesCollection, where('matchId', '==', matchId), limit(1));

    const unsubscribe = onSnapshot(q,
      (querySnapshot) => {
        if (!querySnapshot.empty) {
          const battleDoc = querySnapshot.docs[0];
          const data = battleDoc.data() as BattleData;

          // Set player names
          setPlayer1Name(data.creatorDisplayName || 'Player 1');

          if (data.players?.player2) {
            setPlayer2Name(data.player2DisplayName || 'Player 2');
          } else {
            setPlayer2Name('Waiting for opponent...');
          }
        }
        setLoading(false);
      },
      (error) => {
        // Firestore permissions may not allow direct reads - fall back to defaults
        console.warn('Could not fetch battle data (permissions):', error.message);
        setPlayer1Name('Player 1');
        setPlayer2Name('Player 2');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [matchId, player1DisplayNameProp]);

  if (loading) {
    return <div className={styles.container}>Loading players...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.playerSection}>
        <div className={`${styles.player} ${currentPlayerId === 'player1' ? styles.currentPlayer : ''}`}>
          <div className={styles.playerHeader}>
            <span className={styles.playerLabel}>Player 1</span>
            {player1Connected && <span className={styles.connectedDot} />}
          </div>
          <div className={styles.playerName}>{player1Name}</div>
          {player1Ready && <span className={styles.readyBadge}>Ready</span>}
        </div>
        
        <div className={styles.vs}>VS</div>
        
        <div className={`${styles.player} ${currentPlayerId === 'player2' ? styles.currentPlayer : ''}`}>
          <div className={styles.playerHeader}>
            <span className={styles.playerLabel}>Player 2</span>
            {player2Connected && <span className={styles.connectedDot} />}
          </div>
          <div className={styles.playerName}>{player2Name}</div>
          {player2Ready && <span className={styles.readyBadge}>Ready</span>}
        </div>
      </div>
    </div>
  );
};